import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, DatePicker, Row, Select, Table } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";

import OrderService from "services/order";
import BasicTablePaging from "components/BasicTablePaging/BasicTablePaging"; // chỉnh path đúng dự án
import { LAYOUT } from "constants/layout"; // chỉnh path đúng dự án
import { PriceText } from "components/DataTableText";
import addKeyLocalStorage from "helper/localStorage";
import AffiliateOrderStatsWidgets from "components/AffiliateWidget";
import ButtonExportExcel from "components/ButtonExportExcel";
import { ORDER_PAYMENT_STATUS, AFFILIATE_ORDER_STATUS } from "constants/order";

const { RangePicker } = DatePicker;
const PAGE_SIZE = 20;

const AFFILIATE_ORDER_EXPORT_FIELDS = [
  { key: "index", labelKey: "affiliate.orders.index" },
  { key: "orderName", labelKey: "affiliate.orders.orderName" },
  { key: "totalAmount", labelKey: "affiliate.orders.totalAmount" },
  {
    key: "paymentStatus",
    labelKey: "affiliate.orders.paymentStatus",
    format: (value, row, translate) => {
      const status = ORDER_PAYMENT_STATUS.find((item) => item.value === value);
      return translate(status?.label ?? value);
    },
  },
  {
    key: "orderStatus",
    labelKey: "affiliate.orders.orderStatus",
    format: (value, row, translate) => {
      const status = AFFILIATE_ORDER_STATUS.find((item) => item.value === value);
      return translate(status?.label ?? value);
    },
  },
  { key: "commissionAmount", labelKey: "affiliate.orders.commissionAmount" },
  { key: "referUserFullName", labelKey: "affiliate.orders.referUserFullName" },
  { key: "referStationName", labelKey: "affiliate.orders.referStationName" },
];

const AffiliateOrders = () => {
  const { t: translate } = useTranslation();

  // ----- Filter / pagination state (nguồn dữ liệu duy nhất, cũng là payload gửi API) -----
  const [dataFilter, setDataFilter] = useState({
    startDate: moment().startOf("month").toDate(),
    endDate: moment().endOf("month").toDate(),
    skip: 0,
    limit: PAGE_SIZE,
    filter: {
      orderStatus: undefined,
      paymentStatus: undefined,
    },
    order: {
      key: "createdAt",
      value: "desc"
    }
  });

  // ----- Table data state -----
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ----- Stats widget state -----
  const [affiliateOrderStats, setAffiliateOrderStats] = useState({});
  const [isAffiliateOrderStatsLoading, setIsAffiliateOrderStatsLoading] = useState(false);

  // ----- Fetch table data -----
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OrderService.getListAffiliateOrder({
        startDate: moment(dataFilter.startDate).startOf("day"),
        endDate: moment(dataFilter.endDate).endOf("day"),
        skip: dataFilter.skip,
        limit: dataFilter.limit,
        filter: dataFilter.filter,
      });
      setData(response?.data || []);
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [dataFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleChangePage = (pageNum) => {
    setDataFilter((prev) => ({
      ...prev,
      skip: (pageNum - 1) * prev.limit,
    }));
  };

  const handleChangeFilter = (key, value) => {
    setDataFilter((prev) => ({
      ...prev,
      skip: 0,
      filter: {
        ...prev.filter,
        [key]: value,
      },
    }));
  };

  // ----- Fetch widget stats (kỳ hiện tại + kỳ trước 1 tháng) -----
  const loadStats = useCallback(async () => {
    setIsAffiliateOrderStatsLoading(true);
    try {
      const currentParams = {
        startDate: moment(dataFilter.startDate).startOf("day"),
        endDate: moment(dataFilter.endDate).endOf("day"),
      };
      const previousParams = {
        startDate: moment(dataFilter.startDate).subtract(1, "month").startOf("day"),
        endDate: moment(dataFilter.endDate).subtract(1, "month").endOf("day"),
      };

      const [currentData, previousData] = await Promise.all([
        OrderService.getAffiliateOrderStatistics(currentParams),
        OrderService.getAffiliateOrderStatistics(previousParams),
      ]);

      setAffiliateOrderStats({ current: currentData, previous: previousData });
    } catch (e) {
      console.error(e);
    } finally {
      setIsAffiliateOrderStatsLoading(false);
    }
  }, [dataFilter.startDate, dataFilter.endDate]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // ----- Reload cả bảng lẫn widget -----
  const handleReload = () => {
    fetchOrders();
    loadStats();
  };

  // ----- Export Excel config -----
  const fetchAffiliateOrdersForExport = ({ skip, limit }) =>
    OrderService.getListAffiliateOrder({
      startDate: moment(dataFilter.startDate).startOf("day"),
      endDate: moment(dataFilter.endDate).endOf("day"),
      skip,
      limit,
      filter: dataFilter.filter,
    });

  const getAffiliateOrdersFileName = () => {
    const memberInfo = JSON.parse(window.localStorage.getItem(addKeyLocalStorage("data"))) || {};
    const stationCode = memberInfo.stationCode || memberInfo.stationsId || "ALL";
    const exportTime = moment().format("YYYYMMDD_HHmmss");
    return `Tiếp thị liên kết - Đơn hàng ${stationCode}_${exportTime}.xlsx`;
  };

  // ----- Table columns -----
  const columns = [
    {
      title: translate("affiliate.orders.index"),
      dataIndex: "orderId",
      key: "orderId",
      width: LAYOUT.columns.small,
      align: "center",
      render: (_, __, index) => <span>{dataFilter.skip + index + 1}</span>,
    },
    {
      title: translate("affiliate.orders.orderName"),
      dataIndex: "orderName",
      key: "orderName",
      width: LAYOUT.columns.xlarge,
      align: "center",
      ellipsis: true,
      render: (orderName) => <span>{orderName}</span>,
    },
    {
      title: translate("affiliate.orders.totalAmount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (totalAmount) => <PriceText className="order-amount" price={totalAmount || 0} />,
    },
    {
      title: translate("affiliate.orders.orderStatus"),
      dataIndex: "orderStatus",
      key: "orderStatus",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (status) => {
        const orderStatus = AFFILIATE_ORDER_STATUS.find((item) => item.value === status);
        return (
          <span className={orderStatus?.color}>
            {translate(orderStatus?.label ?? status)}
          </span>
        );
      },
    },
    {
      title: translate("affiliate.orders.paymentStatus"),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (paymentStatus) => {
        const status = ORDER_PAYMENT_STATUS.find(
          (item) => item.value === paymentStatus
        );

        return (
          <span className={status?.color}>
            {translate(status?.label ?? paymentStatus)}
          </span>
        );
      },
    },
    {
      title: translate("affiliate.orders.commissionAmount"),
      dataIndex: "commissionAmount",
      key: "commissionAmount",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (commissionAmount, record) => (
        <PriceText
          className={`order-amount ${record?.paymentStatus === "success" ? "text-success" : ""}`}
          price={commissionAmount || 0}
        />
      ),
    },
    {
      title: translate("affiliate.orders.referUserFullName"),
      dataIndex: "referUserFullName",
      key: "referUserFullName",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (referUserFullName) => <span>{referUserFullName}</span>,
    },
    {
      title: translate("affiliate.orders.referStationName"),
      dataIndex: "referStationName",
      key: "referStationName",
      width: LAYOUT.columns.medium,
      align: "center",
      render: (referStationName) => <span>{referStationName}</span>,
    },
  ];

  return (
    <div>
      <AffiliateOrderStatsWidgets affiliateOrderStats={affiliateOrderStats} loading={isAffiliateOrderStatsLoading} />

      <Row gutter={[24, 24]} align="middle" className="mb-3">
        <Col xs={24} sm={16} md={12} lg={8} xl={4}>
          <RangePicker
            className="w-100"
            format="DD/MM/YYYY"
            allowClear={false}
            value={[
              dataFilter.startDate ? moment(dataFilter.startDate) : null,
              dataFilter.endDate ? moment(dataFilter.endDate) : null,
            ]}
            onChange={(dates) => {
              if (!dates) return;
              setDataFilter((prev) => ({
                ...prev,
                startDate: dates[0] ? dates[0].toDate() : prev.startDate,
                endDate: dates[1] ? dates[1].toDate() : prev.endDate,
                skip: 0,
              }));
            }}
          />
        </Col>

        <Col xs={24} sm={16} md={12} lg={8} xl={4}>
          <Select
            className="w-100"
            placeholder={translate("affiliate.orders.orderStatus")}
            allowClear
            value={dataFilter.filter.orderStatus}
            onChange={(value) => handleChangeFilter("orderStatus", value)}
            options={AFFILIATE_ORDER_STATUS.map((item) => ({
              value: item.value,
              label: translate(item.label),
            }))}
          />
        </Col>

        <Col xs={24} sm={16} md={12} lg={8} xl={4}>
          <Select
            className="w-100"
            placeholder={translate("affiliate.orders.paymentStatus")}
            allowClear
            value={dataFilter.filter.paymentStatus}
            onChange={(value) => handleChangeFilter("paymentStatus", value)}
            options={ORDER_PAYMENT_STATUS.map((item) => ({
              value: item.value,
              label: translate(item.label),
            }))}
          />
        </Col>

        <Col xs={24} sm={16} md={12} lg={8} xl={"auto"} className="d-flex gap-4">
          <ButtonExportExcel
            text={translate("affiliate.actions.export")}
            fetchApi={fetchAffiliateOrdersForExport}
            fields={AFFILIATE_ORDER_EXPORT_FIELDS}
            getFileName={getAffiliateOrdersFileName}
            translate={translate}
          />
          <Button
            type="default"
            icon={<ReloadOutlined />}
            loading={loading}
            disabled={loading}
            onClick={handleReload}
          />
        </Col>
      </Row>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="orderId"
        loading={loading}
        scroll={{ x: 1312 }}
        pagination={false}
      />

      <BasicTablePaging
        handlePaginations={handleChangePage}
        count={data.length < dataFilter.limit}
        currentPage={dataFilter.skip / dataFilter.limit + 1}
      />
    </div>
  );
};

export default AffiliateOrders;